<template>
  <div class="behaviour-effect">
    <character-select
      :selected="state.effect.npcKey"
      :characters="getNpcCharacters(emitter.caseConf)"
      :disabled="readonly"
      :errors="missingSourceCharacterErrors"
      placeholder="Select VIP"
      @input="updateNpc"
    />
    <div class="basic-options">
      <state-input
        :state="animationStateWithEligibleValues"
        :initial-value="state.effect.value"
        :synced-value="state.effect.value"
        :show-label="false"
        width="240px"
        :disabled="readonly"
        class="behaviour-input"
        @input="value => updateBehaviour(value)"
      >
        <template slot="prepend">To</template>
      </state-input>
      <help-tooltip
        v-if="!state.effect.npcKey && !state.effect.value"
        type="warning"
      >
        An option must be selected. Data will not be saved.
      </help-tooltip>
      <help-tooltip v-if="missingValueErrors.length" type="error">
        <div v-for="error in missingValueErrors" :key="error.message">{{ error.message }}</div>
      </help-tooltip>
      <el-card v-if="isOneShot && isRepeating" :body-style="{padding: '6px'}">
        <el-checkbox
          label="Play once"
          class="repeats-checkbox"
          :value="!state.effect.repeats"
          :disabled="!isRepeating"
          @input="v => updatePlayOnce(v)"
        />
      </el-card>
    </div>
  </div>
</template>

<script>
import StateInput from '~lib/common/components/StateInput.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import { getNpcCharacters } from '~lib/common/utils/characters';
import CharacterSelect from './CharacterSelect.vue';
import nodeMixins from './mixins/NodeMixins';
import states from '~lib/common/config/states';

export default {
  name: 'vital-effect',
  components: {
    CharacterSelect,
    StateInput,
    HelpTooltip,
  },
  mixins: [ nodeMixins ],
  props: [ 'readonly', 'emitter', 'ikey', 'getData', 'putData', 'control' ],
  data() {
    return {
      state: {
        isAdvancedOpen: false,
        effect: {
          id: null,
          npcKey: null,
          repeats: true,
          value: null,
          position: null,
          repeatDelay: null,
        },
      },
    };
  },
  computed: {
    missingSourceCharacterErrors() {
      return this.getErrorsById('missingSourceCharacter');
    },
    missingValueErrors() {
      return this.getErrorsById('missingValue');
    },
    behaviourState() {
      const behaviourState = states.find(s => s.id === 'npcAnimation');
      return {
        ...behaviourState,
        possibleValues: behaviourState.possibleValues.filter(
          enumValue =>
            enumValue.meta &&
            (enumValue.meta.isRepeating || enumValue.meta.isOneShot),
        ) || [],
      };
    },
    eligibleAnimationValues() {
      const animationState = this.behaviourState;
      return animationState.possibleValues.filter(
        value =>
          value.meta &&
          value.meta.compatiblePositions &&
          (!value.meta.isMilitaryOnly || this.emitter.isMilitaryOrg || this.isMilitaryAnimation)
      );
    },
    animationStateWithEligibleValues() {
      return {
        ...this.behaviourState,
        possibleValues: this.eligibleAnimationValues,
      };
    },
    isRepeating() {
      return this.behaviourState.possibleValues.find(
        value => value.key === this.state.effect.value &&
                 value.meta && value.meta.isRepeating
      );
    },
    isOneShot() {
      return this.behaviourState.possibleValues.find(
        value => value.key === this.state.effect.value &&
                 value.meta && value.meta.isOneShot
      );
    },
    hasRepeatDelay() {
      const animationEnumValue = this.behaviourState.possibleValues.find(
        enumValue => enumValue.key === this.state.effect.value,
      );
      if (!animationEnumValue) {
        return false;
      }
      return animationEnumValue.meta.hasRepeatDelay;
    },
    isMilitaryAnimation() {
      const animationEnumValue = this.behaviourState.possibleValues.find(
        enumValue => enumValue.key === this.state.effect.value,
      );
      if (!animationEnumValue) {
        return false;
      }
      return animationEnumValue.meta.isMilitaryOnly;
    },
    positionWarning() {
      if (this.warnings && this.warnings.length) {
        for (const warning of this.warnings) {
          if (warning.metadata && warning.metadata.warningId === 'helicopterStance') {
            return warning;
          }
        }
      }
      return null;
    },
  },
  created() {
    this.state.effect = { ...this.state.effect, ...this.getData(this.ikey) };
    this.emitter.trigger('process', this.control);
  },
  methods: {
    getNpcCharacters,
    updateNpc(key) {
      this.state.effect.npcKey = key;
      this.update();
    },
    updateBehaviour(behaviour) {
      this.state.effect.value = behaviour;
      this.state.effect.oneShotValue = this.getOneShotValue(behaviour);
      this.state.effect.repeatDelay = this.hasRepeatDelay ? 0 : null;
      // Adjust repeats to valid value for selection
      if (this.isOneShot && !this.isRepeating) {
        this.state.effect.repeats = false;
      }
      if (!this.isOneShot) {
        this.state.effect.repeats = true;
      }
      this.update();
    },
    getOneShotValue(behaviour) {
      const behaviourState = this.behaviourState.possibleValues.find(
        value => value.key === behaviour
      );
      return behaviourState && behaviourState.meta && behaviourState.meta.oneShotValue;
    },
    update() {
      const { effect } = this.state;
      this.putData(this.ikey, {
        type: effect.repeats ? 'NPC_ANIMATION_REPEAT' : 'NPC_ANIMATION_ONE_SHOT',
        npcKey: effect.npcKey,
        value: effect.value,
        oneShotValue: effect.oneShotValue,
        // Don't save position for now; we set everything to ANY in Cases Service
        // position: 'STAND',
        repeatDelay: effect.repeatDelay,
        repeats: effect.repeats
      });
      this.emitter.trigger('process', this.control);
      this.$forceUpdate();
    },
    updatePlayOnce(playOnce) {
      const repeats = !playOnce;
      this.state.effect.repeats = repeats;
      // Reset selected behaviour if it's not part of the current eligible values
      if (
        !this.behaviourState.possibleValues.find(
          v => v.key === this.state.effect.value,
        )
      ) {
        this.state.effect.value = null;
      }
      if (!repeats) {
        this.state.effect.repeatDelay = null;
      }
      this.update();
    },
    updateRepeatDelay(repeatDelay) {
      // This is a workaround to avoid the input to render a blank string
      // Vue inputs are controlled through watchers, so when the user erases
      // "0" from the input and we fallback the value to 0, the watcher does
      // not retrigger
      this.state.effect.repeatDelay = null;
      this.$nextTick(() => {
        this.state.effect.repeatDelay = parseFloat(repeatDelay) || 0;
        this.update();
      });
    },
  },
};
</script>

<style scoped>
.basic-options {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.duration-input {
  width: 125px;
}

.repeat-delay-input {
  width: 200px;
}

.help-tooltip {
  margin-left: 3px;
}
</style>
